/**
 * packages/core/src/crons.ts
 *
 * 선언적 Cron Jobs
 *
 * New apps should declare one cron definition per export and attach them through
 * defineApi({ crons }). Legacy cronJobs() remains available during migration.
 *
 * @example
 * // gencow/cron-jobs.ts
 * import { cron } from "@gencow/core";
 * import { heartbeat } from "./jobs";
 *
 * export const heartbeatCron = cron
 *   .name("heartbeat")
 *   .interval({ minutes: 15 })
 *   .handler(heartbeat);
 */

import type { GencowProcedureDef } from "./procedure.js";

// ─── 타입 ───────────────────────────────────────────────

export type CronProcedureAction = GencowProcedureDef<"internal", any, any, any>;
export type GencowCronAction = CronProcedureAction | (() => Promise<void>);
export type CronJobAction = string | GencowCronAction;

export interface GencowCronDef {
  /** 크론 잡 이름 */
  name: string;
  /** cron 표현식 (예: "0 2 * * *") */
  pattern: string;
  /** 실행할 internal procedure 또는 로컬 전용 inline handler */
  action: GencowCronAction;
}

export interface CronJobDef {
  /** 크론 잡 이름 */
  name: string;
  /** cron 표현식 (예: "0 2 * * *") */
  pattern: string;
  /** 실행할 액션 또는 핸들러 */
  action: CronJobAction;
}

export interface CronScheduleDef {
  /** 크론 잡 이름 */
  name: string;
  /** cron 표현식 (예: "0 2 * * *") */
  pattern: string;
  /** 실행할 internal procedure name */
  procedureName: string;
}

export interface IntervalOptions {
  /** 분 단위 */
  minutes?: number;
  /** 시간 단위 */
  hours?: number;
  /** 초 단위 */
  seconds?: number;
}

export interface DailyOptions {
  /** 실행 시각 (0-23, 로컬 시간) */
  hour: number;
  /** 분 (0-59, 기본 0) */
  minute?: number;
}

export interface WeeklyOptions {
  /** 요일 (0=일, 1=월, ..., 6=토) */
  dayOfWeek: number;
  /** 시각 (0-23) */
  hour: number;
  /** 분 (0-59, 기본 0) */
  minute?: number;
}

export interface CronJobsBuilder {
  /** 일정 간격으로 실행 */
  interval(name: string, options: IntervalOptions, action: CronJobAction): CronJobsBuilder;
  /** 매일 특정 시각에 실행 */
  daily(name: string, options: DailyOptions, action: CronJobAction): CronJobsBuilder;
  /** 매주 특정 요일/시각에 실행 */
  weekly(name: string, options: WeeklyOptions, action: CronJobAction): CronJobsBuilder;
  /** cron 표현식으로 직접 지정 */
  cron(name: string, pattern: string, action: CronJobAction): CronJobsBuilder;
  /** 등록된 크론 잡 목록 (서버 내부에서 사용) */
  getJobs(): CronJobDef[];
}

export interface CronScheduledBuilder {
  /** 실행할 internal procedure 또는 로컬 전용 inline handler 지정 */
  handler(action: GencowCronAction): GencowCronDef;
}

export interface CronNamedBuilder {
  /** 일정 간격으로 실행 */
  interval(options: IntervalOptions): CronScheduledBuilder;
  /** 매일 특정 시각에 실행 */
  daily(options: DailyOptions): CronScheduledBuilder;
  /** 매주 특정 요일/시각에 실행 */
  weekly(options: WeeklyOptions): CronScheduledBuilder;
  /** cron 표현식으로 직접 지정 */
  cron(pattern: string): CronScheduledBuilder;
}

export interface CronBuilderNamespace {
  /** 크론 잡 이름 지정 */
  name(name: string): CronNamedBuilder;
}

// ─── 빌더 구현 ──────────────────────────────────────────

function createScheduledCronDef(name: string, pattern: string): CronScheduledBuilder {
  return {
    handler(action) {
      if (typeof action === "string") {
        throw new Error("cron.handler() requires procedure.internal ref or inline handler, not string action");
      }
      return { name, pattern, action };
    },
  };
}

function createNamedCronBuilder(name: string): CronNamedBuilder {
  return {
    interval(options) {
      return createScheduledCronDef(name, intervalToPattern(options));
    },

    daily(options) {
      const minute = options.minute ?? 0;
      return createScheduledCronDef(name, `${minute} ${options.hour} * * *`);
    },

    weekly(options) {
      const minute = options.minute ?? 0;
      return createScheduledCronDef(name, `${minute} ${options.hour} * * ${options.dayOfWeek}`);
    },

    cron(pattern) {
      return createScheduledCronDef(name, pattern);
    },
  };
}

export const cron: CronBuilderNamespace = {
  name(name) {
    return createNamedCronBuilder(name);
  },
};

/**
 * 선언적 크론 잡 빌더.
 *
 * @deprecated New apps should use independent `cron.name(...).handler(...)` defs in
 * `defineApi({ crons })`. This builder remains for legacy `gencow/crons.ts`
 * migration only.
 *
 * @example
 * const crons = cronJobs();
 * crons.interval("sync", { minutes: 15 }, "metrics.collect");
 * crons.daily("report", { hour: 9 }, "reports.generate");
 * crons.cron("custom", "0 30 * * *", "custom.handler");
 * export default crons;
 */
export function cronJobs(): CronJobsBuilder {
  const jobs: CronJobDef[] = [];

  const builder: CronJobsBuilder = {
    interval(name, options, action) {
      const pattern = intervalToPattern(options);
      jobs.push({ name, pattern, action });
      return builder;
    },

    daily(name, options, action) {
      const minute = options.minute ?? 0;
      const pattern = `${minute} ${options.hour} * * *`;
      jobs.push({ name, pattern, action });
      return builder;
    },

    weekly(name, options, action) {
      const minute = options.minute ?? 0;
      const pattern = `${minute} ${options.hour} * * ${options.dayOfWeek}`;
      jobs.push({ name, pattern, action });
      return builder;
    },

    cron(name, pattern, action) {
      jobs.push({ name, pattern, action });
      return builder;
    },

    getJobs() {
      return [...jobs];
    },
  };

  return builder;
}

export function isCronJobsBuilder(value: unknown): value is CronJobsBuilder {
  return !!value && typeof value === "object" && typeof (value as { getJobs?: unknown }).getJobs === "function";
}

// ─── 유틸리티 ───────────────────────────────────────────

function intervalToPattern(options: IntervalOptions): string {
  if (options.seconds) {
    // Runtime cron adapters support second-level patterns: "*/N * * * * *"
    return `*/${options.seconds} * * * * *`;
  }
  if (options.minutes) {
    return `*/${options.minutes} * * * *`;
  }
  if (options.hours) {
    return `0 */${options.hours} * * *`;
  }
  throw new Error("interval: minutes, hours, 또는 seconds 중 하나를 지정하세요");
}
